import { ensureSyntaxTree } from "@codemirror/language";
import { EditorSelection } from "@codemirror/state";

import {
    ancestorOfNodeWithType,
    lastAncestorAtEndPosition,
} from "components/organisms/manuscript/editor/latex/tree/ancestors";
import { formattingCommandMap } from "components/organisms/manuscript/editor/latex/tree/formatting";

const skipParentNodeTypes = ["LongArg", "TextArgument", "OpenBrace", "CloseBrace"];

function getParentNode(position, state, assoc = 1) {
    const tree = ensureSyntaxTree(state, 1000);
    let node;
    if (typeof position === "number") {
        node = tree?.resolveInner(position, assoc)?.parent;

        const ancestorAtEndPos = lastAncestorAtEndPosition(node, position);
        if (ancestorAtEndPos?.parent) {
            node = ancestorAtEndPos.parent;
        }
    } else {
        node = position?.parent;
    }

    while (node && skipParentNodeTypes.includes(node.type.name)) {
        node = node.parent;
    }

    return node || undefined;
}

function moveRange(range, newFrom, newTo) {
    const forwards = range.from === range.anchor;
    return forwards ? EditorSelection.range(newFrom, newTo) : EditorSelection.range(newTo, newFrom);
}

function wrapRangeInCommand(state, range, command) {
    const content = state.sliceDoc(range.from, range.to);
    const changes = state.changes([
        {
            from: range.from,
            to: range.to,
            insert: `${command}{${content}}`,
        },
    ]);
    return {
        changes,
        range: moveRange(range, range.from + command.length + 1, range.from + command.length + content.length + 1),
    };
}

function validateReplacement(expected, actual) {
    if (expected !== actual) {
        throw new Error("Replacement in toggleRange failed validation.");
    }
}

function getWrappingAncestor(node, nodeType) {
    for (let ancestor = node; ancestor; ancestor = ancestor.parent) {
        if (ancestor.type.is(nodeType)) {
            return ancestor;
        }
        if (formattingCommandMap[ancestor.type.name]) {
            return null;
        }
    }
    return null;
}

function spansWholeArgument(commandNode, range) {
    const argument = commandNode?.getChild("TextArgument")?.getChild("LongArg");
    return Boolean(argument && argument.from === range.from && argument.to === range.to);
}

function bubbleUpRange(nodeType, node, range) {
    let currentRange = range;
    for (
        let ancestorCommand = ancestorOfNodeWithType(node, "$ToggleTextFormattingCommand");
        spansWholeArgument(ancestorCommand, currentRange);
        ancestorCommand = ancestorOfNodeWithType(ancestorCommand?.parent, "$ToggleTextFormattingCommand")
    ) {
        if (!ancestorCommand) {
            break;
        }
        currentRange = moveRange(currentRange, ancestorCommand.from, ancestorCommand.to);
        if (ancestorCommand.type.is(nodeType)) {
            const argumentNode = ancestorCommand.getChild("TextArgument")?.getChild("LongArg");
            if (!argumentNode) {
                return range;
            }
            return moveRange(range, argumentNode.from, argumentNode.to);
        }
    }

    return range;
}

function adjustRangeIfNeeded(nodeType, range, state) {
    const tree = ensureSyntaxTree(state, 1000);
    if (!tree || tree.length < range.to) {
        return range;
    }

    const nodeLeft = tree.resolveInner(range.from, 1);
    const nodeRight = tree.resolveInner(range.to, -1);
    const parentLeft = getWrappingAncestor(nodeLeft, nodeType);
    const parentRight = getWrappingAncestor(nodeRight, nodeType);

    const parent = getParentNode(nodeLeft, state);
    if (parent?.type.is("$ToggleTextFormattingCommand") && spansWholeArgument(parent, range)) {
        return bubbleUpRange(nodeType, ancestorOfNodeWithType(nodeLeft, "$ToggleTextFormattingCommand"), range);
    }

    if (!parentLeft) {
        return bubbleUpRange(nodeType, ancestorOfNodeWithType(nodeLeft, "$ToggleTextFormattingCommand"), range);
    }
    if (nodeLeft.type.is("$CtrlSeq") && range.from === range.to) {
        const commandNode = nodeLeft.parent?.parent?.parent;
        if (!commandNode) {
            return range;
        }
        return EditorSelection.cursor(commandNode.from);
    }

    let { from, to } = range;
    if (nodeLeft.type.is("$CtrlSeq")) {
        from = nodeLeft.to + 1;
    }
    if (nodeLeft.type.is("OpenBrace")) {
        from = nodeLeft.to;
    }
    if (parentLeft === parentRight && nodeRight.type.is("CloseBrace")) {
        to = nodeRight.from;
    }
    return bubbleUpRange(nodeType, parentLeft, moveRange(range, from, to));
}

export const wrapRanges =
    (prefix, suffix, wrapWholeLine = false, selection) =>
    (view) => {
        if (view.state.readOnly) {
            return false;
        }
        view.dispatch(
            view.state.changeByRange((range) => {
                const insert = { prefix, suffix };

                if (wrapWholeLine) {
                    const line = view.state.doc.lineAt(range.anchor);

                    if (range.empty) {
                        range = EditorSelection.range(line.from, line.to);
                    }

                    if (range.from !== line.from) {
                        insert.prefix = `\n${prefix}`;
                    }

                    if (range.to !== line.to) {
                        insert.suffix = `${suffix}\n`;
                    }
                }

                const content = view.state.sliceDoc(range.from, range.to);
                const changedRange = range.map(
                    view.state.changes([
                        {
                            from: range.from,
                            insert: insert.prefix,
                        },
                    ]),
                    1,
                );

                return {
                    range: selection ? selection(changedRange, view) : changedRange,
                    changes: [
                        {
                            from: range.from,
                            to: range.to,
                            insert: `${insert.prefix}${content}${insert.suffix}`,
                        },
                    ],
                };
            }),
            { scrollIntoView: true },
        );
        return true;
    };

export const toggleRanges = (command) => {
    const nodeType = formattingCommandMap[command];

    return (view) => {
        if (view.state.readOnly) {
            return false;
        }

        view.dispatch(
            view.state.changeByRange((initialRange) => {
                const range = adjustRangeIfNeeded(nodeType, initialRange, view.state);
                const content = view.state.sliceDoc(range.from, range.to);

                const ancestorAtStartOfRange = getParentNode(range.from, view.state, range.from === 0 ? 1 : -1);
                const ancestorAtEndOfRange = range.empty
                    ? ancestorAtStartOfRange
                    : getParentNode(range.to, view.state, range.to === view.state.doc.length ? -1 : 1);

                const tree = ensureSyntaxTree(view.state, 1000);
                const nodeAtFrom = tree?.resolveInner(range.from, 1);
                const nodeAtTo = tree?.resolveInner(range.to, -1);
                const isSingleNodeSelected = nodeAtFrom === nodeAtTo;

                if (!isSingleNodeSelected && ancestorAtStartOfRange !== ancestorAtEndOfRange) {
                    const ancestorAtStartIsWrappingCommand = ancestorAtStartOfRange?.type.is(nodeType);
                    const ancestorAtEndIsWrappingCommand =
                        ancestorAtEndOfRange && ancestorAtEndOfRange.type.is(nodeType);

                    if (
                        ancestorAtStartIsWrappingCommand &&
                        ancestorAtEndIsWrappingCommand &&
                        ancestorAtStartOfRange?.parent?.parent?.parent &&
                        ancestorAtEndOfRange?.parent?.parent?.parent
                    ) {
                        const nextAncestorAtStartOfRange = ancestorAtStartOfRange.parent.parent.parent;
                        const nextAncestorAtEndOfRange = ancestorAtEndOfRange.parent.parent.parent;

                        if (nextAncestorAtStartOfRange === nextAncestorAtEndOfRange) {
                            const textBetweenRanges = view.state.sliceDoc(
                                ancestorAtStartOfRange.to,
                                ancestorAtEndOfRange.from,
                            );
                            const ancestorStartArgumentNode = ancestorAtStartOfRange.lastChild?.getChild("LongArg");
                            const ancestorEndArgumentNode = ancestorAtEndOfRange.lastChild?.getChild("LongArg");
                            if (!ancestorStartArgumentNode || !ancestorEndArgumentNode) {
                                throw new Error("Can't find argument node");
                            }
                            const actualContent = view.state.sliceDoc(
                                ancestorAtStartOfRange.from,
                                ancestorAtEndOfRange.to,
                            );
                            const firstCommandArgument = view.state.sliceDoc(
                                ancestorStartArgumentNode.from,
                                ancestorStartArgumentNode.to,
                            );
                            const secondCommandArgument = view.state.sliceDoc(
                                ancestorEndArgumentNode.from,
                                ancestorEndArgumentNode.to,
                            );
                            validateReplacement(
                                `${command}{${firstCommandArgument}}${textBetweenRanges}${command}{${secondCommandArgument}}`,
                                actualContent,
                            );
                            const changes = view.state.changes([
                                {
                                    from: ancestorAtStartOfRange.from,
                                    to: ancestorAtEndOfRange.to,
                                    insert: `${command}{${firstCommandArgument}${textBetweenRanges}${secondCommandArgument}}`,
                                },
                            ]);
                            return {
                                changes,
                                range: moveRange(range, range.from, range.to - command.length - 2),
                            };
                        }
                    }

                    if (
                        ancestorAtEndIsWrappingCommand &&
                        ancestorAtEndOfRange.parent?.parent?.parent === ancestorAtStartOfRange
                    ) {
                        const contentUpToCommand = view.state.sliceDoc(range.from, ancestorAtEndOfRange.from);
                        const ancestorEndArgumentNode = ancestorAtEndOfRange.lastChild?.getChild("LongArg");
                        if (!ancestorEndArgumentNode) {
                            throw new Error("Can't find argument node");
                        }
                        const commandContent = view.state.sliceDoc(
                            ancestorEndArgumentNode.from,
                            ancestorEndArgumentNode.to,
                        );
                        const actualContent = view.state.sliceDoc(range.from, ancestorAtEndOfRange.to);
                        validateReplacement(`${contentUpToCommand}${command}{${commandContent}}`, actualContent);
                        const changes = view.state.changes([
                            {
                                from: range.from,
                                to: ancestorAtEndOfRange.to,
                                insert: `${command}{${contentUpToCommand}${commandContent}}`,
                            },
                        ]);
                        return {
                            changes,
                            range: moveRange(range, range.from + command.length + 1, range.to),
                        };
                    }

                    if (
                        ancestorAtStartIsWrappingCommand &&
                        ancestorAtStartOfRange &&
                        ancestorAtStartOfRange.parent?.parent?.parent === ancestorAtEndOfRange
                    ) {
                        const contentAfterCommand = view.state.sliceDoc(ancestorAtStartOfRange.to, range.to);
                        const ancestorStartArgumentNode = ancestorAtStartOfRange.lastChild?.getChild("LongArg");
                        if (!ancestorStartArgumentNode) {
                            throw new Error("Can't find argument node");
                        }
                        const commandContent = view.state.sliceDoc(
                            ancestorStartArgumentNode.from,
                            ancestorStartArgumentNode.to,
                        );
                        const actualContent = view.state.sliceDoc(ancestorAtStartOfRange.from, range.to);
                        validateReplacement(`${command}{${commandContent}}${contentAfterCommand}`, actualContent);
                        const changes = view.state.changes([
                            {
                                from: ancestorAtStartOfRange.from,
                                to: range.to,
                                insert: `${command}{${commandContent}${contentAfterCommand}}`,
                            },
                        ]);
                        return {
                            changes,
                            range: moveRange(range, range.from, range.to - 1),
                        };
                    }

                    return { range };
                }

                const ancestor = ancestorAtStartOfRange;

                if (content.includes("\n\n")) {
                    return { range };
                }

                const isCursorBeforeAncestor =
                    range.empty && ancestor && range.from === ancestor.from && ancestor.type.is(nodeType);

                if (isCursorBeforeAncestor || !ancestor?.type.is(nodeType)) {
                    return wrapRangeInCommand(view.state, range, command);
                }

                const argumentNode = ancestor.lastChild?.getChild("LongArg");
                if (!argumentNode) {
                    throw new Error("Can't find argument node");
                }

                if (range.from === argumentNode.from && range.to !== argumentNode.to) {
                    const textAfterSelection = view.state.sliceDoc(range.to, argumentNode.to);
                    const wholeCommand = view.state.sliceDoc(ancestor.from, ancestor.to);
                    validateReplacement(`${command}{${content}${textAfterSelection}}`, wholeCommand);
                    const changes = view.state.changes([
                        {
                            from: ancestor.from,
                            to: ancestor.to,
                            insert: `${content}${command}{${textAfterSelection}}`,
                        },
                    ]);
                    return {
                        range: moveRange(range, range.from - command.length - 1, range.to - command.length - 1),
                        changes,
                    };
                }

                if (range.to === argumentNode.to && range.from !== argumentNode.from) {
                    const textBeforeSelection = view.state.sliceDoc(ancestor.from, range.from);
                    const wholeCommand = view.state.sliceDoc(ancestor.from, ancestor.to);
                    validateReplacement(`${textBeforeSelection}${content}}`, wholeCommand);
                    const changes = view.state.changes([
                        {
                            from: ancestor.from,
                            to: ancestor.to,
                            insert: `${textBeforeSelection}}${content}`,
                        },
                    ]);
                    return {
                        range: moveRange(range, range.from + 1, range.to + 1),
                        changes,
                    };
                }

                if (range.from !== argumentNode.from && range.to !== argumentNode.to) {
                    const textBeforeSelection = view.state.sliceDoc(ancestor.from, range.from);
                    const textAfterSelection = view.state.sliceDoc(range.to, ancestor.to);
                    const wholeCommand = view.state.sliceDoc(ancestor.from, ancestor.to);
                    validateReplacement(`${textBeforeSelection}${content}${textAfterSelection}`, wholeCommand);
                    const changes = view.state.changes([
                        {
                            from: ancestor.from,
                            to: ancestor.to,
                            insert: `${textBeforeSelection}}${content}${command}{${textAfterSelection}`,
                        },
                    ]);

                    return {
                        range: moveRange(range, range.from + 1, range.to + 1),
                        changes,
                    };
                }

                if (spansWholeArgument(ancestor, range)) {
                    const argumentContent = view.state.sliceDoc(argumentNode.from, argumentNode.to);
                    const wholeCommand = view.state.sliceDoc(ancestor.from, ancestor.to);
                    validateReplacement(`${command}{${content}}`, wholeCommand);
                    const changes = view.state.changes([
                        {
                            from: ancestor.from,
                            to: ancestor.to,
                            insert: argumentContent,
                        },
                    ]);

                    return {
                        range: moveRange(range, range.from - command.length - 1, range.to - command.length - 1),
                        changes,
                    };
                }

                return wrapRangeInCommand(view.state, range, command);
            }),
            { scrollIntoView: true },
        );
        return true;
    };
};
